import canvasUtils from '../utils';
import StyleReader from './StyleReader';
import styleWriter from './stylewriter';

export default class StyleController {
  constructor(canvas, element) {
    this.canvas = canvas;
    this.element = element;
    this.values = {};
    this.breakpoints = {};
    this.states = [];
    this.nodeValues = {};
  }

  setNodes() {
    if (!this.element) return;
    this.referenceNodes = { default: this.element };
    if (this.element.element && this.element.textEditable) {
      this.referenceNodes = {};
      const referenceParent = this.element.element.cloneNode(true);
      this.element.childrenSelected.forEach((child) => {
        this.referenceNodes[child] = referenceParent.querySelector(
          `.gp-component-id-${child}`
        );
      });
    } else if (this.element.element) {
      this.referenceNodes = { default: this.element.element.cloneNode(true) };
    }
  }

  getStyles(properties, breakpoints, states, stylesheets) {
    this.setNodes();
    const config = {
      element: this.element,
      stylesheets,
    };
    if (!config.stylesheets.length) {
      config.stylesheets = [...this.canvas.styleSheets].filter((stylesheet) => {
        let include = stylesheet.ownerNode.classList.contains(
          `style-${this.element.id}`
        );
        this.element.childrenSelected.forEach((child) => {
          if (stylesheet.ownerNode.classList.contains(`style-${child}`)) {
            include = true;
          }
        });
        return include;
      });
    }

    if (this.element.eventBus) {
      this.element.eventBus.$emit('canvas-get-styles', config);
    }
    this.breakpoints = breakpoints;
    this.states = states;
    this.properties = properties;
    let allValues = null;
    const isCountdown = this.element?.configType === 'countdown';
    if (isCountdown) {
      this.styleMultiple();
    }
    for (const id in this.referenceNodes) {
      if (this.referenceNodes[id]) {
        const styleReader = new StyleReader(breakpoints, states, properties);
        this.nodeValues[id] = styleReader.getStyles(
          this.referenceNodes[id],
          config.stylesheets
        );
        if (!allValues) {
          allValues = JSON.parse(JSON.stringify(this.nodeValues[id]));
        } else if (!isCountdown) {
          canvasUtils.mergeOverlapping(allValues, this.nodeValues[id]);
        }
      }
    }
    this.values = allValues;
    return this.values;
  }

  setStyle(property, value, options = {}) {
    const breakpoint = options.breakpoint || 'all';
    const state = options.state || 'none';
    this.values[breakpoint][state][property] = value;
    if (options.cascade) {
      this.cascadeStyles(property, breakpoint, state);
    }
  }

  getStyle(property, options = {}) {
    const breakpoint = options.breakpoint || 'all';
    const state = options.state || 'none';
    let value = this.values[breakpoint]?.[state]?.[property] || null;
    const screenIndex = canvasUtils
      .breakpointLabels(this.breakpoints)
      .indexOf(breakpoint);
    for (let i = screenIndex; i > -1; i--) {
      if (value === null) {
        value =
          this.values[canvasUtils.breakpointLabels(this.breakpoints)[i]][state][
            property
          ];
      }
    }
    return value;
  }

  cascadeStyles(property, breakpoint, state) {
    const sizes = canvasUtils.breakpointLabels(this.breakpoints).reverse();
    const selectedIndex = sizes.indexOf(breakpoint);
    const value = this.values[sizes[selectedIndex]][state][property];
    for (let i = selectedIndex; i < sizes.length; i++) {
      // adjustments for responsive sizing in cascading elements
      if (property === 'width') {
        let size;
        if (sizes[i].includes('1200px')) size = 1200;
        if (sizes[i].includes('992px')) size = 992;
        if (sizes[i].includes('768px')) size = 768;
        if (sizes[i].includes('640px')) size = 600;
        if (sizes[i].includes('all')) size = 400;
        const val = value.slice(0, -2);
        if (val > size) this.values[sizes[i]][state][property] = '100%';
        else this.values[sizes[i]][state][property] = value;
      } else this.values[sizes[i]][state][property] = value;
    }
  }

  setStyles(values, excludeMatchingStylesheets) {
    if (!this.element) return;
    const isCountdown = this.element?.configType === 'countdown';

    if (isCountdown) {
      if (
        !this.element.configComponent
          ?.querySelector('.data-gp-countdown-switch input')
          ?.hasAttribute('checked')
      ) {
        this.setNodes();
      } else {
        this.styleMultiple();
      }
    } else if (!this.referenceNodes) this.setNodes();
    const config = {
      excludeMatchingStylesheets: excludeMatchingStylesheets || [],
      element: this.element,
    };
    if (this.element.eventBus) {
      this.element.eventBus.$emit('canvas-set-styles', config);
    }
    for (const nodeId in this.referenceNodes) {
      let toWrite = values || this.values;
      const minimumReferenceNodesLength = isCountdown ? 0 : 1;
      if (
        Object.keys(this.referenceNodes).length > minimumReferenceNodesLength
      ) {
        canvasUtils.mergeOverwritten(this.nodeValues[nodeId], toWrite);
        toWrite = this.nodeValues[nodeId];
      }
      this.removeExcludedStyles(
        toWrite,
        this.referenceNodes[nodeId],
        config.excludeMatchingStylesheets
      );
      const id = nodeId === 'default' ? this.element.id : nodeId;
      let selector = `.gp-component-id-${id}[data-gp-component]`;
      if (isCountdown) {
        selector = `.gp-component-id-${id}`;
      }
      if (this.element.textEditable) {
        selector = `[data-gp-text] .gp-component-id-${nodeId}`;
      }
      if (this.element.componentType === 'Body') {
        selector = 'body';
      }
      this.canvas.querySelector(selector).removeAttribute('style');
      const content = styleWriter.getStylesheet(toWrite, selector, true);
      this.updateStylesheet(
        content,
        `style-${selector === 'body' ? 'gp-body' : id}`,
        id
      );
    }
    canvasUtils.triggerEvent(
      this.element.getElement().ownerDocument.defaultView,
      'resize'
    );
  }

  removeExcludedStyles(values, node, styleSheetsToExclude) {
    const styleReader = new StyleReader(
      this.breakpoints,
      this.states,
      this.properties
    );
    const toExclude = styleReader.getStyles(node, styleSheetsToExclude);
    canvasUtils.removeOverlapping(values, toExclude);
  }

  updateStylesheet(content, className, componentStyled) {
    this.canvas.querySelectorAll(`.${className}`).forEach((style) => {
      style.remove();
    });
    const styles = document.createElement('style');
    if (componentStyled) {
      styles.setAttribute('data-gp-styled-element', componentStyled);
    }
    styles.classList.add(className);
    styles.innerHTML = content;
    this.canvas.getElementById('blocks-container').prepend(styles);
  }

  styleMultiple() {
    const componentType = this.element.configComponent;
    const childComponents = [...componentType.querySelectorAll(':scope > div')];

    if (
      this.element.element.hasAttribute('gp-countdown-days') ||
      this.element.element.hasAttribute('gp-countdown-hrs') ||
      this.element.element.hasAttribute('gp-countdown-mins') ||
      this.element.element.hasAttribute('gp-countdown-secs')
    ) {
      childComponents.forEach((node) => {
        this.referenceNodes[
          node.querySelector('div').getAttribute('data-gp-component-id')
        ] = node.querySelector('div');
      });
    } else if (this.element.element.classList.contains('gp-text-container')) {
      childComponents.forEach((node) => {
        let textNode = node.querySelector('div:last-child p');
        if (!textNode) {
          textNode = node.querySelector('div:last-child > div');
        }
        this.referenceNodes[canvasUtils.getComponentIdFromClass(textNode)] =
          textNode;
      });
    } else {
      childComponents.forEach((node) => {
        this.referenceNodes[node.getAttribute('data-gp-component-id')] = node;
      });
    }
  }
}
